import { html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_ICAP } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  StorageIcapSearchableKeywords,
  StorageProtectionIcapFormValue,
} from '../../../../utils/uiSettings/storageProtection/storage-protection.types';
import {
  getScanActionsFormOptions,
  getStorageIcapSearchableKeywords,
  MAX_ARCHIVE_DEPTH,
  MAX_ARCHIVE_SIZE,
  MAX_CONGESTION_CONTROL_CONNECTIONS,
  MAX_PORT_VALUE,
  MIN_ARCHIVE_DEPTH,
  MIN_ARCHIVE_SIZE,
  MIN_CONGESTION_CONTROL_CONNECTIONS,
  MIN_PORT_VALUE,
  STORAGE_PROTECTION_ICAP_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/storageProtection/storage-protection.values';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { MaxValidator } from '../../../../../../../ui/gzForm/validators/max.validator';
import { MinValidator } from '../../../../../../../ui/gzForm/validators/min.validator';
import { GzInfoBoxWebComponent } from '../../../../../../../ui/gzInfoBox';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { BehaviorSubject, combineLatest, Observable, skip } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { FormEvent } from '../../../../../../../ui/gzForm/formField/types/form-event.enum';
import { FormValidator } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { CongestionControl } from '@nirvana/shared/dtos/types/policies/common';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_ICAP)
export class GzStorageProtectionIcapWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  StorageProtectionIcapFormValue,
  StorageIcapSearchableKeywords
> {
  /**
   * Path for Storage Protection Icap section.
   */
  protected path = {
    moduleId: ModuleId.STORAGE_PROTECTION,
    sectionId: SectionId.STORAGE_PROTECTION_ICAP,
  };

  /**
   * Searchable keywords for Storage Protection Icap section.
   */
  protected searchableKeywords = getStorageIcapSearchableKeywords();

  // storage protection toggle and content
  @query('#storage-protection-icap-toggle')
  private storageProtectionToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#storage-protection-icap-info-text')
  private storageProtectionIcapInfoText: GzTextWebComponent;
  @query('#storage-protection-icap-content')
  private storageProtectionContent: HTMLElement;

  // on-access scanning section
  @query('#service-name-form-field')
  private serviceNameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#listen-non-secure-checkbox-form-field')
  private listenNonSecureCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#listen-non-secure-input-form-field')
  private listenNonSecureInputFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#listen-secure-checkbox-form-field')
  private listenSecureCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#listen-secure-input-form-field')
  private listenSecureInputFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#no-option-selected-notice')
  private noOptionSelectedNotice: GzInfoBoxWebComponent;
  /**
   * Subject to emit the valid state of the port section.
   */
  private portSectionValidSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  /**
   * Observable to get the valid state of the port section.
   */
  public portSectionValid$: Observable<boolean> = this.portSectionValidSubject.pipe(distinctUntilChanged());

  // archive scanning settings section
  @query('#scan-archive-checkbox-form-field')
  private scanArchiveCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#scan-archive-content')
  private scanArchiveContent: HTMLElement;
  @query('#scan-archive-max-size-form-field')
  private scanArchiveMaxSizeFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#scan-archive-max-depth-form-field')
  private scanArchiveMaxDepthFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

  // congestion control section
  @query('#congestion-control-mode-form-field')
  private congestionControlModeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#congestion-control-max-connections-form-field')
  private congestionControlMaxConnectionsFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

  // scan actions section
  @query('#scan-actions-default-action-form-field')
  private scanActionsDefaultActionFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  // booleans to check if ports are initialized with values
  private isNonSecurePortValueSet = false;
  private isSecurePortValueSet = false;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="storage-protection-icap-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageProtection].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
        ><span
          >${this.searchableKeywords[StorageIcapSearchableKeywords.StorageProtection].translatedLabel}</span
        ></gz-toggle
      >
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageIcap].label}"
      id="storage-protection-icap-info-text"
      fontSize="12"
      fontWeight="400"
      class="infoText"
      >${translateFn(
        'These settings apply to Security Servers when used as a scanning service for ICAP-compatible storage devices.'
      )}</gz-text
    >`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="storage-protection-icap-content">
        <div>
          <gz-title
            searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageOnAccessScanning].label}"
            class="module-subtitle"
            id="storage-protection-icap-on-access-scanning-title"
          >
            ${this.searchableKeywords[StorageIcapSearchableKeywords.StorageOnAccessScanning].translatedLabel}
          </gz-title>

          <gz-title
            searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageConnectionSettings].label}"
            fontSize="11"
            fontWeight="600"
            class="form-subtitle"
            >${this.searchableKeywords[StorageIcapSearchableKeywords.StorageConnectionSettings]
              .translatedLabel}</gz-title
          >

          <gz-form-field
            searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageServiceName].label}"
            id="service-name-form-field"
            class="label-mw-250"
            label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageServiceName].translatedLabel}"
          >
            <gz-input gz-form-input inputWidth="${InputWidthValues.MEDIUM}"></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <div class="checkbox-with-input">
            <gz-form-field class="label-mw-250 checkbox-width" id="listen-non-secure-checkbox-form-field">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageNonSecurePort].label}"
                label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageNonSecurePort].translatedLabel +
                ':'}"
                gz-form-input
                @change="${this.onNonSecurePortCheckboxChange.bind(this)}"
              ></gz-checkbox>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <gz-form-field class="label-mw-250" id="listen-non-secure-input-form-field">
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.MEDIUM}"
                @change="${this.checkPortSectionValidity.bind(this)}"
              ></gz-input>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>

          <div class="checkbox-with-input">
            <gz-form-field class="label-mw-250 checkbox-width" id="listen-secure-checkbox-form-field">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageSecurePort].label}"
                label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageSecurePort].translatedLabel +
                ':'}"
                gz-form-input
                @change="${this.onSecurePortCheckboxChange.bind(this)}"
              ></gz-checkbox>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <gz-form-field class="label-mw-250" id="listen-secure-input-form-field">
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.MEDIUM}"
                @change="${this.checkPortSectionValidity.bind(this)}"
              ></gz-input>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>

          <gz-info-box class="hidden" severity="warning" id="no-option-selected-notice"></gz-info-box>
        </div>

        <div>
          <gz-title
            searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageArchiveScanningSettings]
              .label}"
            fontSize="11"
            fontWeight="600"
            class="form-subtitle"
            >${this.searchableKeywords[StorageIcapSearchableKeywords.StorageArchiveScanningSettings]
              .translatedLabel}</gz-title
          >

          <gz-form-field class="label-mw-250 checkbox-width" id="scan-archive-checkbox-form-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageScanArchive].label}"
              label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageScanArchive].translatedLabel}"
              gz-form-input
              @change="${e => toggleDisabled(this.scanArchiveContent, !e.detail.checked)}"
            ></gz-checkbox>
            <gz-text fontSize="12" fontWeight="400" class="helper-text mt-1"
              >${translateFn('Archives that exceed the configured values will not be scanned.')}</gz-text
            >
          </gz-form-field>

          <div id="scan-archive-content" class="helper-text">
            <gz-form-field
              searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageArchiveMaxSize].label}"
              class="label-mw-250"
              id="scan-archive-max-size-form-field"
              label="${translateFn('Archive maximum size (MB)')}"
            >
              <gz-input-number
                gz-form-input
                inputWidth="${InputWidthValues.MEDIUM}"
                min="${MIN_ARCHIVE_SIZE}"
                max="${MAX_ARCHIVE_SIZE}"
              ></gz-input-number>
            </gz-form-field>

            <gz-form-field
              searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageArchiveMaxDepth].label}"
              class="label-mw-250"
              id="scan-archive-max-depth-form-field"
              label="${translateFn('Archive maximum depth (levels)')}"
            >
              <gz-input-number
                gz-form-input
                inputWidth="${InputWidthValues.MEDIUM}"
                min="${MIN_ARCHIVE_DEPTH}"
                max="${MAX_ARCHIVE_DEPTH}"
              ></gz-input-number>
            </gz-form-field>
          </div>
        </div>

        <div>
          <gz-title
            searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageCongestionControl].label}"
            fontSize="11"
            fontWeight="600"
            class="form-subtitle"
            >${this.searchableKeywords[StorageIcapSearchableKeywords.StorageCongestionControl]
              .translatedLabel}</gz-title
          >

          <gz-form-field id="congestion-control-mode-form-field">
            <gz-radio-group
              gz-form-input
              class="d-flex flex-column"
              @radioSelectionChanged=${e =>
                toggleDisabled(
                  this.congestionControlMaxConnectionsFormField,
                  e.detail.selectedValue === CongestionControl.DROP_WHEN_SVA_IS_OVERLOADED
                )}
            >
              <div class="radio">
                <gz-radio
                  searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageDropConnection]
                    .label}"
                  label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageDropConnection]
                    .translatedLabel}"
                  .value=${CongestionControl.DROP_WHEN_SVA_IS_OVERLOADED}
                ></gz-radio>
              </div>
              <gz-text fontSize="12" fontWeight="400" class="helper-text mb-2"
                >${translateFn(
                  'When a Security Server instance is overloaded, the storage device connects to the next one, if available. It is recommended that you define at least two Security Server instances for scanning storage devices.'
                )}</gz-text
              >

              <div class="radio">
                <gz-radio
                  searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageMaxConnections]
                    .label}"
                  label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageMaxConnections]
                    .translatedLabel + ':'}"
                  .value=${CongestionControl.RESTRICT_MAX_CONNECTIONS}
                ></gz-radio>

                <gz-form-field id="congestion-control-max-connections-form-field">
                  <gz-input-number
                    gz-form-input
                    min="${MIN_CONGESTION_CONTROL_CONNECTIONS}"
                    max="${MAX_CONGESTION_CONTROL_CONNECTIONS}"
                  ></gz-input-number>
                </gz-form-field>
              </div>
              <gz-text fontSize="12" fontWeight="400" class="helper-text"
                >${translateFn(
                  'Security Server denies any new connection if the specified number is exceeded.'
                )}</gz-text
              >
            </gz-radio-group>
          </gz-form-field>
        </div>

        <div>
          <gz-title
            searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageScanActions].label}"
            fontSize="11"
            fontWeight="600"
            class="form-subtitle"
            >${this.searchableKeywords[StorageIcapSearchableKeywords.StorageScanActions].translatedLabel}</gz-title
          >

          <gz-form-field
            searchable-label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageDefaultAction].label}"
            class="label-mw-250"
            id="scan-actions-default-action-form-field"
            label="${this.searchableKeywords[StorageIcapSearchableKeywords.StorageDefaultAction].translatedLabel}"
          >
            <gz-dropdown gz-form-input options="${JSON.stringify(getScanActionsFormOptions())}"></gz-dropdown>
          </gz-form-field>
          <gz-text fontSize="12" fontWeight="400"
            >${translateFn(
              'This action is recommended by Security Server to the storage device. When selected, the Disinfect action may delete the entire file if a large part of it is infected.'
            )}</gz-text
          >
        </div>
      </div>
    `;
  }

  /**
   * Subscribe to validity event received from form group and port section.
   */
  protected subscribeToValidityEvent() {
    // combine into a single observable (skip first value, because it is always true)
    const formGroupValidSubscription = combineLatest([this.formGroup.valid$, this.portSectionValid$])
      .pipe(skip(1))
      .subscribe({
        next: ([formGroupValid, portSectionValid]) => {
          this.changeValidityIfNecessary(formGroupValid && portSectionValid);
        },
      });

    this.subscriptions.push(formGroupValidSubscription);
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.storageProtection.icap
   */
  protected getValueFromManager(): StorageProtectionIcapFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.STORAGE_PROTECTION]: { [SectionId.STORAGE_PROTECTION_ICAP]: STORAGE_PROTECTION_ICAP_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.STORAGE_PROTECTION]: {
          [SectionId.STORAGE_PROTECTION_ICAP]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.storageProtection.icap'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        storageProtection: {
          icap: {
            enabled: this.storageProtectionToggle,
            serviceName: this.serviceNameFormField,
            enablePort: this.listenNonSecureCheckboxFormField,
            listenPort: this.listenNonSecureInputFormField,
            enableSslPort: this.listenSecureCheckboxFormField,
            listenSslPort: this.listenSecureInputFormField,
            archiveScan: FormGroup.build({
              enabled: this.scanArchiveCheckboxFormField,
              archiveMaxSize: this.scanArchiveMaxSizeFormField,
              archiveMaxDepth: this.scanArchiveMaxDepthFormField,
            }),
            congestionControl: FormGroup.build({
              mode: this.congestionControlModeFormField,
              maxConnections: this.congestionControlMaxConnectionsFormField,
            }),
            scanAction: FormGroup.build({
              defaultAction: this.scanActionsDefaultActionFormField,
            }),
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    const listenPort = isNaN(this.valueT.uiSettings.storageProtection.icap.listenPort)
      ? ''
      : this.valueT.uiSettings.storageProtection.icap.listenPort.toString();
    const listenSslPort = isNaN(this.valueT.uiSettings.storageProtection.icap.listenSslPort)
      ? ''
      : this.valueT.uiSettings.storageProtection.icap.listenSslPort.toString();

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        storageProtection: {
          icap: {
            enabled: this.valueT.uiSettings.storageProtection.icap.enabled,
            serviceName: this.valueT.uiSettings.storageProtection.icap.serviceName,
            enablePort: this.valueT.uiSettings.storageProtection.icap.enablePort,
            listenPort: listenPort,
            enableSslPort: this.valueT.uiSettings.storageProtection.icap.enableSslPort,
            listenSslPort: listenSslPort,
            archiveScan: {
              enabled: this.valueT.uiSettings.storageProtection.icap.archiveScan.enabled,
              archiveMaxSize: this.valueT.uiSettings.storageProtection.icap.archiveScan.archiveMaxSize,
              archiveMaxDepth: this.valueT.uiSettings.storageProtection.icap.archiveScan.archiveMaxDepth,
            },
            congestionControl: {
              mode: this.valueT.uiSettings.storageProtection.icap.congestionControl.mode,
              maxConnections: this.valueT.uiSettings.storageProtection.icap.congestionControl.maxConnections,
            },
            scanAction: {
              defaultAction: this.valueT.uiSettings.storageProtection.icap.scanAction.defaultAction,
            },
          },
        },
      },
    };
  }

  /**
   * Set validators for form fields.
   */
  protected setValidators() {
    // port validators
    const portValidators = [
      MaxValidator(
        MAX_PORT_VALUE,
        translateFn('Specify a port number between {minValue} and {maxValue}.', {
          minValue: MIN_PORT_VALUE,
          maxValue: MAX_PORT_VALUE,
        })
      ),
      MinValidator(
        MIN_PORT_VALUE,
        translateFn('Specify a port number between {minValue} and {maxValue}.', {
          minValue: MIN_PORT_VALUE,
          maxValue: MAX_PORT_VALUE,
        })
      ),
    ];

    // add event listeners for setting validators on form fields (only once)
    this.setServiceNameValidators();
    this.setListenNonSecureInputFormFieldValidators(portValidators);
    this.setListenSecureInputFormFieldValidators(portValidators);
  }

  private setServiceNameValidators() {
    this.serviceNameFormField.Validators = [RequiredValidator(translateFn('Specify the service name.'))];
  }

  private setListenNonSecureInputFormFieldValidators(portValidators: FormValidator[]) {
    this.isNonSecurePortValueSet = true;
    this.checkPortSectionValidity();
    this.listenNonSecureInputFormField.Validators = [
      RequiredValidator(
        translateFn('Specify the port on which Security Server should listen for non-secure connections.')
      ),
      ...portValidators,
    ];
  }

  private setListenSecureInputFormFieldValidators(portValidators: FormValidator[]) {
    this.isSecurePortValueSet = true;
    this.checkPortSectionValidity();
    this.listenSecureInputFormField.Validators = [
      RequiredValidator(translateFn('Specify the port on which Security Server should listen for secure connections.')),
      ...portValidators,
    ];
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // change to number
    const listenPort = _.get(value, ['uiSettings', 'storageProtection', 'icap', 'listenPort'], '');
    _.set(value, ['uiSettings', 'storageProtection', 'icap', 'listenPort'], parseInt(listenPort, 10));

    // change to number
    const listenSslPort = _.get(value, ['uiSettings', 'storageProtection', 'icap', 'listenSslPort'], '');
    _.set(value, ['uiSettings', 'storageProtection', 'icap', 'listenSslPort'], parseInt(listenSslPort, 10));

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'storageProtection', 'icap'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.storageProtectionIcapInfoText, !event.detail.checked);
    toggleDisabled(this.storageProtectionContent, !event.detail.checked);
  }

  /**
   * Enable/Disable non-secure port field and check validity.
   * @param event
   */
  private onNonSecurePortCheckboxChange(event: CustomEvent) {
    toggleDisabled(this.listenNonSecureInputFormField, !event.detail.checked);
    this.checkPortSectionValidity();
  }

  /**
   * Enable/Disable secure port field and check validity.
   * @param event
   */
  private onSecurePortCheckboxChange(event: CustomEvent) {
    toggleDisabled(this.listenSecureInputFormField, !event.detail.checked);
    this.checkPortSectionValidity();
  }

  /**
   * Check port section validity
   */
  private checkPortSectionValidity() {
    // wait for both fields to be initialized with values before checking validity
    if (!this.isSecurePortValueSet || !this.isNonSecurePortValueSet) {
      return;
    }

    // at least one option is selected
    const atLeastOneSelected = this.listenSecureCheckboxFormField.value || this.listenNonSecureCheckboxFormField.value;
    // has different values
    const hasDifferentValues = this.listenSecureInputFormField.value !== this.listenNonSecureInputFormField.value;

    const isValid = atLeastOneSelected && hasDifferentValues;
    // show notice
    toggleVisibility(this.noOptionSelectedNotice, !isValid);
    // change validity
    this.portSectionValidSubject.next(isValid);

    if (isValid) {
      return;
    }

    // no option selected
    if (!atLeastOneSelected) {
      this.noOptionSelectedNotice.innerText = translateFn('Select at least one option.');
      return;
    }

    // same values
    if (!hasDifferentValues) {
      this.noOptionSelectedNotice.innerText = translateFn('Secure and non-secure ports must have different values.');
      return;
    }
  }

  /**
   * Override reset method, because we need to reset value of boolean fields.
   */
  public reset() {
    super.reset();

    // reset flags
    this.isNonSecurePortValueSet = false;
    this.isSecurePortValueSet = false;

    // reset validators
    this.serviceNameFormField.Validators = [];
    this.listenNonSecureInputFormField.Validators = [];
    this.listenSecureInputFormField.Validators = [];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_ICAP: GzStorageProtectionIcapWebComponent;
  }
}
